/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useCallback, useContext, useState } from 'react'
import { useServiceContext } from '../../../root'
import { documentTypesApiService } from '../../../services/api/api.documenttypes'
import { RcmTableDataContext } from '../../../components/rcm-table/RcmTableDataContext'
import { RcmTableDataWrapper } from '../../../components/rcm-table/RcmTableDataWrapper'
import RcmTableFilterTrigger from '../../../components/rcm-table/RcmTableFilterTrigger'
import Loader from '../../../components/shared/Loader'
import PageMenu from './PageMenu'
import TableFilter from './TableFilter'
import Table from './Table'
import ModalEdit from './Actions/ModalEdit'

const DocumentTypesContent: React.FC = () => {
  const tableContext = useContext(RcmTableDataContext)
  const context = useServiceContext()
  const { translate } = context.service.filter
  const [editDocumentType, setEditDocumentType] = useState<any>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  const handleOpenModal = (property?: any) => {
    setEditDocumentType(property || null)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setEditDocumentType(null)
  }

  return (
    <>
      <Loader blocking={isExporting}>
        <div>
          <div className="row">
            <div className="col-xs-12">
              <div className="d-flex flex-wrap" style={{ paddingBottom: '1%' }}>
                <h1 className="align-left align-top" id="7caa630f-1984-43bd-9c7d-d1d058bd389c" style={{ margin: '0' }}>
                  {translate('TYPES_TITLE')}
                </h1>

                <div className="align-right align-bottom d-flex">
                  <PageMenu
                    onAddDocumentType={() => handleOpenModal()}
                    onExportStart={() => setIsExporting(true)}
                    onExportEnd={() => setIsExporting(false)}
                  />
                  <RcmTableFilterTrigger />
                </div>
              </div>

              <TableFilter />
              <Table
                onEditDocumentType={handleOpenModal}
                onDeleteSuccess={() => tableContext?.refreshTable()}
                onExportStart={() => setIsExporting(true)}
                onExportEnd={() => setIsExporting(false)}
              />
            </div>
          </div>
        </div>
        {isModalOpen && <ModalEdit documentType={editDocumentType || undefined} onClose={handleCloseModal} />}
      </Loader>
    </>
  )
}

const DocumentTypesPage: React.FC = () => {
  // Create loadData function for RcmTableDataWrapper
  const loadData = useCallback(async (pagingParams: any, filter: any) => {
    try {
      // Extract IDs from filter.types array - each item has structure { value: id, label: name }
      const typeIds = filter?.types && filter.types.length > 0 ? filter.types.map((t: any) => t.value || t.id || t) : []

      const params = {
        pageStart: pagingParams.pageStart || 0,
        pageSize: pagingParams.pageSize || 15,
        sortBy: pagingParams.sortBy || 'NAME',
        sortDesc: pagingParams.sortDesc !== undefined ? pagingParams.sortDesc : false,
        ...(typeIds.length > 0 ? { id: typeIds } : {})
      }

      const result: any = await documentTypesApiService.getAllForTable(params)

      return {
        elements: result.elements || [],
        totalElements: result.totalElements || 0
      }
    } catch (error) {
      return {
        elements: [],
        totalElements: 0
      }
    }
  }, [])

  return (
    <div className="mcc-content-frame theme-sandman">
      <RcmTableDataWrapper
        loadData={loadData}
        defaultSortBy="NAME"
        defaultSortDesc={false}
        persistenceKey="settingsDocumentTypes"
        doPreload={true}
      >
        <DocumentTypesContent />
      </RcmTableDataWrapper>
    </div>
  )
}

export default DocumentTypesPage
